import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { animationFields, mediaRadiusField } from '../_shared/universal.js';
/**
 * multirow — a VERBATIM port of Shopify/Shrine's `multirow` section. It stacks a list of `row` blocks,
 * each an `image-with-text` split (media column + content column) whose image side ALTERNATES down the
 * column. Every knob mirrors Shrine's `multirow.liquid` schema 1:1 (image height/width, heading size,
 * text style, button style, desktop content position/alignment, image layout, per-row + section colour
 * schemes with custom colours, mobile media position/alignment, padding); the `row` block mirrors Shrine's
 * `row` block (image OR video, caption, highlightable heading, body richtext, button label + link).
 *
 * The HTML the Component emits is Shrine's exact class structure (`.multirow > .multirow__inner.page-width
 * > .image-with-text.…isolate.…same-colors > .image-with-text__grid.grid.grid--gapless.grid--1-col
 * .grid--{2|3}-col-tablet[.image-with-text__grid--reverse] > media-item + text-item`) and the scoped CSS
 * is Shrine's `base.css` rules for those classes ported dimensionally verbatim, with colours remapped to
 * the aquilixShop `--aqx-scheme-*` / `--aqx-theme-*` token contract (the codebase uses full colours, not
 * Shrine's rgb-triples — see theme.css). ATC-button + video-play-button rows are deferred (they need cart
 * context); the media `video` field renders a plain muted-autoplay loop.
 */
const SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
    { value: 'custom', label: 'Custom' },
];
export const multirowSchema = {
    type: 'multirow',
    name: 'Multirow',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Layout ──────────────────────────────────────────────────────────────
        {
            // The whole painted band (background + rows) takes N% of the page width, centred; 100 = full bleed.
            id: 'section_width',
            type: 'range',
            label: 'Section width',
            group: 'Layout',
            min: 30,
            max: 100,
            step: 1,
            unit: '%',
            default: 100,
        },
        {
            id: 'image_height',
            type: 'select',
            label: 'Image height',
            group: 'Layout',
            default: 'medium',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            id: 'desktop_image_width',
            type: 'select',
            label: 'Desktop image width',
            group: 'Layout',
            default: 'medium',
            info: 'Applies only to desktop. Image is always full width on mobile.',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            // Shrine is gapless: the two columns touch. This pins them to OPPOSITE edges of the row instead
            // (flex space-between) — the free space lands between image and text and scales with the screen.
            id: 'spread_columns',
            type: 'checkbox',
            label: 'Push image and text apart',
            group: 'Layout',
            default: false,
            info: 'Pins the image and the text to opposite edges of the row, with the free space between them. No effect when the columns fill the row (medium image width) or on mobile (rows stack).',
        },
        {
            // How much of the row's FREE space sits between the columns (not a fixed size, so it scales with
            // every screen): 100 = fully apart at the row edges, 0 = touching, in-between = the text slides
            // back toward the image and the remainder of the space moves to its outer side.
            id: 'spread_space',
            type: 'range',
            label: 'Space between image and text',
            group: 'Layout',
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 100,
            visibleWhen: { field: 'spread_columns', equals: true },
            info: 'Share of the free space placed between the two columns. 100% pushes them fully apart; lower values bring the text back toward the image.',
        },
        {
            // Only meaningful when spread is on: without spread the text column GROWS to fill the row
            // (verbatim Shrine), so a width control would fight the base layout.
            id: 'desktop_text_width',
            type: 'select',
            label: 'Desktop text width',
            group: 'Layout',
            default: 'small',
            visibleWhen: { field: 'spread_columns', equals: true },
            info: 'Default for all rows — each row can override it in its own settings.',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            id: 'image_layout',
            type: 'select',
            label: 'Desktop image placement',
            group: 'Layout',
            default: 'alternate-left',
            info: 'Alternate flips the image side on every other row.',
            options: [
                { value: 'alternate-left', label: 'Alternate, image first' },
                { value: 'alternate-right', label: 'Alternate, image last' },
                { value: 'align-left', label: 'Image always first' },
                { value: 'align-right', label: 'Image always last' },
            ],
        },
        {
            id: 'desktop_content_position',
            type: 'select',
            label: 'Desktop content position',
            group: 'Layout',
            default: 'middle',
            info: 'Position is automatically optimized for mobile.',
            options: [
                { value: 'top', label: 'Top' },
                { value: 'middle', label: 'Middle' },
                { value: 'bottom', label: 'Bottom' },
            ],
        },
        {
            id: 'desktop_content_alignment',
            type: 'select',
            label: 'Desktop content alignment',
            group: 'Layout',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        // ── Content style ─────────────────────────────────────────────────────────
        {
            id: 'heading_size',
            type: 'select',
            label: 'Heading size',
            group: 'Content',
            default: 'h1',
            options: [
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
            ],
        },
        {
            id: 'text_style',
            type: 'select',
            label: 'Text style',
            group: 'Content',
            default: 'body',
            options: [
                { value: 'body', label: 'Body' },
                { value: 'subtitle', label: 'Subtitle' },
            ],
        },
        {
            id: 'button_style',
            type: 'select',
            label: 'Button style',
            group: 'Content',
            default: 'secondary',
            options: [
                { value: 'primary', label: 'Solid' },
                { value: 'secondary', label: 'Outline' },
            ],
        },
        // ── Colors: section band ────────────────────────────────────────────────
        {
            id: 'section_color_scheme',
            type: 'select',
            label: 'Section color scheme',
            group: 'Colors',
            default: 'background-1',
            options: SCHEME_OPTIONS,
        },
        {
            id: 'custom_section_colors_background',
            type: 'color',
            label: 'Custom section background',
            group: 'Colors',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'section_color_scheme', equals: 'custom' },
        },
        // ── Colors: rows (media + content) ──────────────────────────────────────
        {
            id: 'row_color_scheme',
            type: 'select',
            label: 'Rows color scheme',
            group: 'Colors',
            default: 'background-1',
            options: SCHEME_OPTIONS,
        },
        {
            id: 'custom_colors_background',
            type: 'color',
            label: 'Custom rows background',
            group: 'Colors',
            default: { ref: 'custom', value: '#2E2A39' },
            visibleWhen: { field: 'row_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_colors_text',
            type: 'color',
            label: 'Custom rows text',
            group: 'Colors',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'row_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_colors_solid_button_background',
            type: 'color',
            label: 'Custom solid button background',
            group: 'Colors',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'row_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_colors_solid_button_text',
            type: 'color',
            label: 'Custom solid button label',
            group: 'Colors',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'row_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_colors_outline_button',
            type: 'color',
            label: 'Custom outline button color',
            group: 'Colors',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'row_color_scheme', equals: 'custom' },
        },
        // ── Mobile ──────────────────────────────────────────────────────────────
        {
            id: 'mobile_full_media_width',
            type: 'checkbox',
            label: 'Full media width on mobile',
            group: 'Mobile',
            default: false,
        },
        {
            id: 'mobile_direction',
            type: 'select',
            label: 'Mobile media position',
            group: 'Mobile',
            default: 'normal',
            options: [
                { value: 'normal', label: 'Media first' },
                { value: 'reverse', label: 'Media second' },
            ],
        },
        {
            id: 'mobile_content_alignment',
            type: 'select',
            label: 'Mobile content alignment',
            group: 'Mobile',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        mediaRadiusField,
        // ── Section padding ─────────────────────────────────────────────────────
        // Shrine range 0–100 step 4, default 36; mobile padding = 0.75× desktop (see Component).
        {
            id: 'padding_top',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'padding_bottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    // The bespoke `row` block — Shrine's `row` (addable only inside multirow). One media slot + caption +
    // highlightable heading + body + button. Uniform by construction: all chrome/scheme is section-level.
    blocks: [
        {
            type: 'row',
            name: 'Row',
            max: 20,
            fields: [
                { id: 'image', type: 'image', label: 'Image', group: 'Media' },
                {
                    id: 'video',
                    type: 'video',
                    label: 'Video',
                    group: 'Media',
                    info: 'Shown instead of the image. Use a short muted MP4/WebM instead of a GIF.',
                },
                playButtonField({
                    defaultOn: false,
                    group: 'Media',
                    visibleWhen: { field: 'video', empty: false },
                }),
                {
                    id: 'caption',
                    type: 'text',
                    label: 'Caption',
                    group: 'Content',
                    default: 'Nouveauté',
                    defaultKey: 'section.new_arrival',
                    maxLength: 120,
                    // ÉCRITE PAR L'IA : la légende d'une rangée annonce ce que la rangée raconte (« Au lit,
                    // devant la télé »), elle n'est pas une étiquette d'interface. Sans ce drapeau, les trois
                    // rangées d'une page composée s'annoncent toutes « Nouveauté ».
                    aiWritable: true,
                },
                {
                    id: 'title',
                    type: 'richtext',
                    label: 'Heading',
                    group: 'Content',
                    default: { format: 'html', html: 'Titre de la ligne' },
                    info: 'Bold a word to color it with the highlight color below.',
                },
                {
                    id: 'title_highlight_color',
                    type: 'color',
                    label: 'Heading highlight color',
                    group: 'Content',
                    default: { ref: 'theme', token: 'accent' },
                },
                {
                    id: 'text',
                    type: 'richtext',
                    label: 'Text',
                    group: 'Content',
                    default: {
                        format: 'html',
                        html: '<p>Associez du texte à une image pour mettre en avant un produit, une collection ou un article. Ajoutez des détails sur la disponibilité, le style ou même un avis client.</p>',
                    },
                },
                {
                    id: 'button_label',
                    type: 'text',
                    label: 'Button label',
                    group: 'Content',
                    default: 'Nom du bouton',
                    defaultKey: 'section.button_label',
                    // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
                    aiWritable: true,
                    maxLength: 60,
                    info: 'Leave blank to hide the button.',
                },
                { id: 'button_link', type: 'link', label: 'Button link', group: 'Content' },
                {
                    // Per-row override of the section's "Desktop text width" (spread mode only — without
                    // spread the text grows to fill the row, verbatim Shrine).
                    id: 'text_width',
                    type: 'select',
                    label: 'Text width',
                    group: 'Layout',
                    default: 'default',
                    info: 'Only applies when "Push image and text apart" is on in the section settings.',
                    options: [
                        { value: 'default', label: 'Same as section' },
                        { value: 'small', label: 'Small' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'large', label: 'Large' },
                    ],
                },
            ],
        },
    ],
    maxBlocks: 20,
};
// The default matches Shrine's multirow preset exactly: 3 rows, medium image, alternate-left, h1 heading,
// outline button, background-1 for both schemes — i.e. the render pasted from Shopify. Placeholder copy is
// French to match the store (sellers always replace demo copy; the STRUCTURE + CONFIG are 1:1 with Shrine).
const rowPreset = () => ({
    title: { format: 'html', html: 'Titre de la ligne' },
    title_highlight_color: { ref: 'theme', token: 'accent' },
    text: {
        format: 'html',
        html: '<p>Associez du texte à une image pour mettre en avant un produit, une collection ou un article. Ajoutez des détails sur la disponibilité, le style ou même un avis client.</p>',
    },
});
export const multirowPresets = [
    {
        name: 'Multirow',
        settings: {
            image_height: 'medium',
            desktop_image_width: 'medium',
            image_layout: 'alternate-left',
            desktop_content_position: 'middle',
            desktop_content_alignment: 'left',
            heading_size: 'h1',
            text_style: 'body',
            button_style: 'secondary',
            section_color_scheme: 'background-1',
            row_color_scheme: 'background-1',
            mobile_full_media_width: false,
            mobile_direction: 'normal',
            mobile_content_alignment: 'left',
            padding_top: 36,
            padding_bottom: 36,
        },
        blocks: [
            { type: 'row', settings: rowPreset() },
            { type: 'row', settings: rowPreset() },
            { type: 'row', settings: rowPreset() },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const multirowMeta = {
    label: 'Multirow',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map